import React from 'react';
import * as reduxForm from 'redux-form';

import { ConfirmEmailLink } from 'ecs-react-components/dist/index.es';
import history from '../../../history';
import * as TYPES from './auth-actiontypes';
import * as api from './auth-api';
import { logoffAuditRequest } from '../audit/audit-middleware';
import { loadPatient } from '../patient/patient-middleware';
import * as MsgBarActions from '../system-shared/message-bar-action-creator';
import {
  confirmEmailRequestByUserId,
  loadUserProfile,
  loadTermsOfUse,
} from '../user-profile/user-profile-middleware';
import { loadFacility } from '../facility/facility-middleware';
import { getStateFromSession, clearUserSession, persistStateToSession } from '../../webstorage';
import { putPreferredLanguage, putPreferredLanguageByPatient,
  loadPreferredLanguage } from '../language/language-middleware';
import { questionnairesOriginUpdated } from '../questionnaires/questionnaires-action-creator';


export const LOCAL_STORAGE_KEY = 'patient:auth';
const NATIVE_LOGOUT_AUDIT_TYPE = 'logoffauditevent';
const KIOSK_LOGOUT_AUDIT_TYPE = 'kiosklogoffauditevent';
const LINKUSER_LOGOUT_AUDIT_TYPE = 'urllinklogoffauditevent';
const SSO_LOGOUT_AUDIT_TYPE = 'ssologoffauditevent';


const initialState = {
  isAuthenticating: true,
  isAuthenticated: false,
  access_token: null,
  refresh_token: null,
  expires: null,
  isRequestingRefreshToken: false,
  userName: null,
};

const loginRequestAction = (creds) => {
  const state = initialState;
  if (creds) {
    state.userName = creds.username;
  }
  return {
    type: TYPES.LOGIN_REQUEST,
    state,
  };
};

const kioskLoginRequestAction = (creds) => {
  const state = initialState;
  if (creds) {
    state.userName = creds.username;
  }
  return {
    type: TYPES.KIOSK_LOGIN_REQUEST,
    state,
  };
};

export const getState = () => (
  getStateFromSession(LOCAL_STORAGE_KEY, initialState)
);

export const loginSuccess = (response) => {
  const data = response || {};
  const state = {
    isAuthenticating: false,
    isAuthenticated: true,
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    loginType: 'userNamePassword',
    expires: new Date((Date.now() + (data.expires_in * 1000)) - 15000),
  };

  persistStateToSession(LOCAL_STORAGE_KEY, state);

  return {
    type: TYPES.LOGIN_SUCCESS,
    state,
  };
};

export const kioskLoginSuccess = (response) => {
  const data = response || {};
  const state = {
    isAuthenticating: false,
    isAuthenticated: true,
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    loginType: 'patientKioskManagement',
    expires: new Date((Date.now() + (data.expires_in * 1000)) - 15000),
  };

  persistStateToSession(LOCAL_STORAGE_KEY, state);

  return {
    type: TYPES.KIOSK_LOGIN_SUCCESS,
    state,
  };
};

export const singleSignOnLoginSuccess = (response) => {
  const state = {
    isAuthenticating: false,
    isAuthenticated: true,
    access_token: response.access_token,
    refresh_token: response.refresh_token,
    loginType: 'singleSignOn',
    expires: new Date((Date.now() + (response.expires_in * 1000)) - 15000),
  };

  persistStateToSession(LOCAL_STORAGE_KEY, state);

  return {
    type: TYPES.SINGLESIGNON_LOGIN_SUCCESS,
    state,
  };
};

export const patientKioskUserLoginSuccess = (response, loginType) => {
  const state = {
    isAuthenticating: false,
    isAuthenticated: true,
    access_token: response.access_token,
    refresh_token: response.refresh_token,
    loginType,
    expires: new Date((Date.now() + (response.expires_in * 1000)) - 15000),
  };

  persistStateToSession(LOCAL_STORAGE_KEY, state);

  return {
    type: TYPES.PATIENTKIOSKUSER_LOGIN_SUCCESS,
    state,
  };
};

const processSessionLogout = (dispatch, callback) => {
  clearUserSession()
    .then(
      () => {
        dispatch({ type: TYPES.LOGOUT });
        dispatch(loadFacility(document.URL));
        if (callback) {
          callback();
        }
      }
    );
};

export const redirectToLogout = (loginType) => {
  const publicPath = window.equicareLocalConfiguration.publicPath;
  let target = publicPath ? publicPath + 'login' : '/login';
  if (loginType && loginType.toLowerCase() === 'singlesignon') {
    target = publicPath ? publicPath + 'ssoLogout' : '/ssoLogout';
  }
  if (loginType && ['patientkioskmanagement', 'patientkioskuser'].indexOf(loginType.toLowerCase()) > -1) {
    target = publicPath ? publicPath + 'kioskLogin' : '/kioskLogin';
  }
  location.replace(target);
};

export const logout = (fromTimeout) => (dispatch, completeStore) => {
  const loginType = completeStore().profile.auth.loginType;
  const store = completeStore();
  const patientId = store && store.profile && store.profile.patient ? store.profile.patient.patientId : null;

  let logoutType = NATIVE_LOGOUT_AUDIT_TYPE;

  if (loginType) {
    switch (loginType.toLowerCase()) {
      case 'singlesignon':
        logoutType = SSO_LOGOUT_AUDIT_TYPE;
        break;
      case 'patientkioskuser':
        logoutType = KIOSK_LOGOUT_AUDIT_TYPE;
        break;
      case 'patientlinkuser':
        logoutType = LINKUSER_LOGOUT_AUDIT_TYPE;
        break;
      default:
        break;
    }
  }

  const processLogout = () => {
    processSessionLogout(dispatch, () => {
      redirectToLogout(loginType);
    });
  };

  const includeLogoffAuditEvent = !fromTimeout;
  if (includeLogoffAuditEvent) {
    return dispatch(logoffAuditRequest(logoutType, patientId))
      .then(() => {
        processLogout();
      })
      .catch(() => {
        processLogout();
      });
  }
  processLogout();
  return Promise.resolve();
};


export const logoutConfirmEmail = () => (dispatch) => {
  clearUserSession()
    .then(
      () => {
        dispatch({ type: TYPES.LOGOUT });
      }
    );
};

export const loginFailure = (message) => ({
  type: TYPES.LOGIN_FAILURE,
  state: initialState,
  error: message,
});

export const kioskLoginFailure = (message) => ({
  type: TYPES.KIOSK_LOGIN_FAILURE,
  state: initialState,
  error: message,
});

export const singleSignOnLoginFailure = (message) => ({
  type: TYPES.SINGLESIGNON_LOGIN_FAILURE,
  state: initialState,
  error: message,
});

export const loginLockedOut = (message) => ({
  type: TYPES.LOGIN_LOCKEDOUT,
  state: initialState,
  error: message,
});

export const kioskLoginLockedOut = (message) => ({
  type: TYPES.KIOSK_LOGIN_LOCKEDOUT,
  state: initialState,
  error: message,
});

const mapErrorCode = (errorCode) => {
  let newErrCode;
  switch (errorCode) {
    case 'invalid_grant':
      newErrCode = 'invalidgrant';
      break;
    case 'locked_out':
      newErrCode = 'lockedout';
      break;
    case 'password_change_required':
      newErrCode = 'passwordChangeRequired';
      break;
    default:
      break;
  }

  return newErrCode;
};

export const navigateToLogin = () => {
  const publicPath = window.equicareLocalConfiguration.publicPath;
  location.replace(publicPath ? publicPath + 'login' : '/login');
};


// Reset Password Logic
export const openForgotPasswordRequest = () =>
  history.push('reset-password');

const resetPasswordRequestAction = {
  type: TYPES.RESET_PASSWORD_REQUEST,
  state: initialState,
};

export const resetPasswordRequest = (username, language) =>
  (dispatch) => {
    dispatch(resetPasswordRequestAction);
    const navigateToLoginAndNotify = () => {
      dispatch(MsgBarActions.showSuccessMessage('resetPasswordSuccess'));
      navigateToLogin();
    };
    return api.resetPassword(username, language).then(
      () => navigateToLoginAndNotify(),
      () => navigateToLoginAndNotify()
    );
  };

// Refresh Token Logic

const updateTokenRequestAction = () => {
  const state = {
    isAuthenticating: false,
    isAuthenticated: true,
    access_token: getState().access_token,
    refresh_token: getState().refresh_token,
    isRequestingRefreshToken: true,
    expires: getState().expires,
    loginType: getState().loginType,
  };

  persistStateToSession(LOCAL_STORAGE_KEY, state);

  return {
    type: TYPES.TOKEN_UPDATE_REQUEST,
    state,
  };
};

const updateTokenSuccessAction = (responseData) => {
  const state = {
    isAuthenticating: false,
    isAuthenticated: true,
    access_token: responseData.access_token,
    refresh_token: responseData.refresh_token,
    isRequestingRefreshToken: false,
    expires: new Date((Date.now() + (responseData.expires_in * 1000)) - 15000),
    loginType: getState().loginType,
  };

  persistStateToSession(LOCAL_STORAGE_KEY, state);

  return {
    type: TYPES.TOKEN_UPDATE_SUCCESS,
    state,
  };
};

const updateTokenFailureAction = () =>
  ({
    type: TYPES.TOKEN_UPDATE_FAILURE,
    state: null,
  });

const updateToken = () => (dispatch) => {
  const refreshToken = getState().refresh_token;

  dispatch(updateTokenRequestAction());

  return api.getRefreshedToken({ refresh_token: refreshToken }).then(
    (response) => dispatch(updateTokenSuccessAction(response)),
    () => {
      dispatch(updateTokenFailureAction());
      processSessionLogout(dispatch);
    }
  );
};

export const getNewAccessTokenFromForcedRefresh = () => (dispatch) =>
  dispatch(updateToken()).then(
    () => {
      const accessToken = getState().access_token;
      return accessToken;
    }
  );

const processSuccessfulPasswordChange = (dispatch, formName) => {
  dispatch(MsgBarActions.showSuccessMessage('updatePasswordSuccess'));
  dispatch(reduxForm.blur(formName, 'oldPwd', ''));
  dispatch(reduxForm.blur(formName, 'newPwd', ''));
  dispatch(reduxForm.blur(formName, 'confirmNewPwd', ''));
};

const processErrorPasswordChange = (error, dispatch, formName) => {
  switch (error.data.message) {
    case 'invalid_old_password':
      dispatch(MsgBarActions.showErrorMessage('invalidOldPassword'));
      dispatch(reduxForm.stopSubmit(formName,
        {
          _error: 'invalidOldPassword',
        }
      ));
      dispatch(reduxForm.blur(formName, 'oldPwd', ''));
      dispatch(reduxForm.blur(formName, 'newPwd', ''));
      dispatch(reduxForm.blur(formName, 'confirmNewPwd', ''));
      dispatch(reduxForm.focus(formName, 'oldPwd'));
      break;
    case 'invalid_grant':
      dispatch(loginFailure('invalidgrant'));
      dispatch(MsgBarActions.showErrorMessage('invalidgrant'));
      dispatch(reduxForm.stopSubmit(formName,
        {
          _error: 'Login failed!',
        }
      ));
      break;
    case 'password_must_be_different':
      dispatch(MsgBarActions.showErrorMessage('passwordMustBeDifferent'));
      break;
    case 'confirm_mismatch':
      dispatch(MsgBarActions.showErrorMessage('confirmMismatch'));
      break;
    case 'lockedout':
      dispatch(loginLockedOut(error.data || error.message));
      break;
    default:
      dispatch(MsgBarActions.showErrorMessage('unspecified'));
      break;
  }
};

export const updatePasswordRequestById = (userId, oldPwd, newPwd, confirmNewPwd, formName) =>
  (dispatch) => api.updatePasswordById(userId, oldPwd, newPwd, confirmNewPwd).then(
    () => processSuccessfulPasswordChange(dispatch, formName),
    (axiosResponse) => {
      const error = axiosResponse.response;
      processErrorPasswordChange(error, dispatch, formName);
    }
  );

export const updatePasswordRequestByUserName = (userName, oldPwd, newPwd, confirmPwd, formName) =>
  (dispatch) => api.updatePasswordByUserName(userName, oldPwd, newPwd, confirmPwd).then(
    () => {
      processSuccessfulPasswordChange(dispatch, formName);
      navigateToLogin();
    },
    (axiosResponse) => {
      const error = axiosResponse.response;
      processErrorPasswordChange(error, dispatch, formName);
    }
  );

export const singleSignOnLoginRequest = (singleSignOnIdentification, redirectTo) =>
  (dispatch, storeState) => {
    dispatch(loginRequestAction);
    return api.singleSignOnLogin(singleSignOnIdentification).then(
      (response) => {
        dispatch(singleSignOnLoginSuccess(response));
        return dispatch(loadUserProfile())
          .then((userInfo) => {
            if (userInfo) {
              if (!userInfo.isEulaAccepted) {
                dispatch(loadTermsOfUse(storeState().facility.facilityId,
                  storeState() && storeState().language ? storeState().language.languageCode : null));
                history.push('/termsOfUse');
                return;
              }
            }
            if (redirectTo) {
              // Select the "dashboard" tab after logging in
              history.push(redirectTo === '/' ? '/dashboard' : redirectTo);
            }
          });
      },
      (axiosResponse) => {
        const error = axiosResponse.response;
        const errorCode = error.data ? mapErrorCode(error.data.error) : error.message;
        switch (errorCode) {
          default:
            dispatch(singleSignOnLoginFailure(errorCode));
            break;
        }

        history.push('/singleSignOnFailed');
      }
    );
  };

const patientKioskUserLoggedIn = (auth, redirectTo, patientId, loginType) =>
  (dispatch, getStoreState) => {
    dispatch(patientKioskUserLoginSuccess(auth, loginType));
    return dispatch(loadUserProfile(loginType))
      .then((userInfo) => {
        if (redirectTo) {
          if (!userInfo) {
            dispatch(loadPatient(patientId));
          }

          const kioskPatientId = userInfo && userInfo.patientId ? userInfo.patientId : patientId;
          if (loginType === 'patientLinkUser') {
            const state = getStoreState();
            if (state && kioskPatientId) {
              dispatch(putPreferredLanguageByPatient(kioskPatientId, state.language.languageCode));
            }
          } else {
            dispatch(loadPreferredLanguage(kioskPatientId));
          }
          // Select the "questionnaires" tab after logging in
          const patientKioskPage = '/patientKioskQuestionnaires';
          dispatch(questionnairesOriginUpdated(
            patientKioskPage,
            '/kiosk-questionnaire-scoring',
            '/complete-kiosk-questionnaire'
          ));
          history.push(redirectTo === '/' ? patientKioskPage + '?startIfOnlyOne=true' : redirectTo);
        }
      });
  };

export const patientKioskUserLoginRequest = (singleSignOnIdentification, redirectTo, patientId) =>
  (dispatch) => {
    dispatch(loginRequestAction);
    return api.patientKioskUserLogin(singleSignOnIdentification).then(
      (response) => {
        dispatch(patientKioskUserLoggedIn(response, redirectTo, patientId, 'patientKioskUser'));
      },
      (axiosResponse) => {
        const error = axiosResponse.response;
        const errorCode = error.data ? mapErrorCode(error.data.error) : error.message;
        switch (errorCode) {
          default:
            dispatch(singleSignOnLoginFailure(errorCode));
            break;
        }

        history.push('/kiosklogin');
      }
    );
  };

export const patientLinkUserLoginRequest = (creds, patientId) =>
  (dispatch) => api.kioskLogin(creds).then(
    (response) => {
      dispatch(patientKioskUserLoggedIn(response, '/', patientId, 'patientLinkUser'));
    },
    (axiosResponse) => {
      const error = axiosResponse.response;
      let errorCode = error.data ? mapErrorCode(error.data.error) : error.message;
      const parameters = [error.data ? error.data.error_description : undefined];

      switch (errorCode) {
        case 'lockedout':
          dispatch(kioskLoginLockedOut(error.data || error.message));
          break;
        case 'passwordChangeRequired':
          history.push('/update-password');
          return error;
        case 'invalidgrant':
          if (parameters && parameters.length) {
            switch (parameters[0]) {
              case 'username_password_incorrect':
                errorCode = 'invalidlastnamebirthday';
                break;
              case 'link_expired':
                errorCode = 'linkexpired';
                break;
              default:
                break;
            }
          }
          break;
        default:
          dispatch(kioskLoginFailure(errorCode));
          break;
      }

      dispatch(reduxForm.stopSubmit('linkUserLoginForm',
        {
          _error: 'Login failed!',
        }
      ));

      dispatch(MsgBarActions.showErrorMessage(errorCode, parameters));
      return error;
    }
  );


export const loginRequest = (creds, redirectTo, bypassConfirmEmailWarning) =>
  (dispatch, storeState) => {
    const loginCallBack = (userInfo) => {
      const sState = storeState();
      if (!userInfo.isEulaAccepted) {
        dispatch(loadTermsOfUse(sState.facility.facilityId,
          sState && sState.language ? sState.language.languageCode : null));
        history.push('/termsOfUse');
        return;
      }

      if (redirectTo) {
        history.push(redirectTo === '/' ? '/dashboard' : redirectTo);
      }
    };

    const sState = storeState();
    dispatch(loginRequestAction(creds));
    return api.login(creds).then(
      (response) => {
        dispatch(loginSuccess(response));
        return dispatch(loadUserProfile('defaultPatientPortal', loginCallBack))
          .then((userInfo) => {
            if (userInfo) {
              if (sState && sState.language) {
                dispatch(putPreferredLanguage(userInfo.userId, sState.language.languageCode, false));
              }

              // check to see if we need to place the confirm email message up
              if (userInfo &&
                  userInfo.email &&
                  !userInfo.isEmailConfirmed &&
                  !bypassConfirmEmailWarning) {
                const userEmail = userInfo.email;
                const link = (<ConfirmEmailLink
                  onClickMethod={confirmEmailRequestByUserId(
                    dispatch,
                    userInfo.userId,
                    userEmail,
                    sState && sState.language ? sState.language.languageCode : null)}
                />);

                const parameters = [userEmail, link];
                dispatch(MsgBarActions.showStickyWarningMessage('confirmEmailRequired', parameters));
              }
            }
          });
      },
      (axiosResponse) => {
        const error = axiosResponse.response;
        const errorCode = error.data ? mapErrorCode(error.data.error) : error.message;
        const parameters = [error.data ? error.data.error_description : undefined];

        switch (errorCode) {
          case 'lockedout':
            dispatch(loginLockedOut(error.data || error.message));
            break;
          case 'passwordChangeRequired':
            history.push('/update-password');
            return error;
          default:
            dispatch(loginFailure(errorCode));
            break;
        }

        dispatch(reduxForm.stopSubmit('loginform',
          {
            username: 'Invalid user',
            password: 'Invalid password',
            _error: 'Login failed!',
          }
        ));

        dispatch(MsgBarActions.showErrorMessage(errorCode, parameters));
        return error;
      }
    );
  };

export const kioskLoginRequest = (creds, redirectTo) =>
  (dispatch) => {
    dispatch(kioskLoginRequestAction(creds));
    return api.kioskLogin(creds).then(
      (response) => {
        dispatch(kioskLoginSuccess(response));
        return dispatch(loadUserProfile())
          .then(() => {
            if (redirectTo) {
              // Select the "dashboard" tab after logging in
              history.push(redirectTo === '/' ? '/kioskQuestionnaires' : redirectTo);
            }
          });
      },
      (axiosResponse) => {
        const error = axiosResponse.response;
        const errorCode = error.data ? mapErrorCode(error.data.error) : error.message;
        const parameters = [error.data ? error.data.error_description : undefined];

        switch (errorCode) {
          case 'lockedout':
            dispatch(kioskLoginLockedOut(error.data || error.message));
            break;
          case 'passwordChangeRequired':
            history.push('/update-password');
            return error;
          default:
            dispatch(kioskLoginFailure(errorCode));
            break;
        }

        dispatch(reduxForm.stopSubmit('kioskLoginForm',
          {
            username: 'Invalid user',
            password: 'Invalid password',
            _error: 'Login failed!',
          }
        ));

        dispatch(MsgBarActions.showErrorMessage(errorCode, parameters));
        return error;
      }
    );
  };


export const initiatePatientKioskUserLogon = (patientId) => (dispatch) => {
  const state = initialState;

  dispatch({
    type: TYPES.INITIATE_PATIENT_KIOSK_USER_LOGIN,
    state,
  });

  return api.initiatePatientKioskUserLogon(patientId).then(
    (response) => {
      // log the current user off, *then* change the location of the window to the response
      processSessionLogout(dispatch);
      const publicPath = window.equicareLocalConfiguration.publicPath;
      const target = publicPath ? publicPath + 'patientKioskUserLogin' : '/patientKioskUserLogin';
      const formattedResponse = `${response.facilityUrl}${target}?ptId=${patientId}&data=${response.data}`;

      window.location.replace(formattedResponse);
    },
    () => {
      dispatch(MsgBarActions.showErrorMessage('unspecified'));
    }
  );
};

export const confirmEmailRequest = (creds) => (dispatch, getCurrentState) =>
  dispatch(loginRequest(creds, '/', true))
    .then(
      (error) => {
        if (!error) {
          api.getIsUserIdValid(
            getCurrentState().profile.user.userId, creds.username, creds.encryptedUserId
          )
            .then(
              (response) => {
                if (response.data.userValidation) {
                  api.putConfirmEmail(getCurrentState().profile.user.userId)
                    .then(
                      () => dispatch(MsgBarActions.showStickySuccessMessage('confirmEmailSuccess')),
                      () => dispatch(MsgBarActions.showStickyErrorMessage('confirmEmailError'))
                    );
                } else {
                  dispatch(MsgBarActions.showStickyErrorMessage('confirmEmailError'));
                }
              }
            )
            .catch(
              () => dispatch(MsgBarActions.showStickyErrorMessage('confirmEmailError'))
            );
        }
      }
    );
